import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Accordion from '../../components/base/Accordion';

export default function FAQPage() {
  useEffect(() => {
    // SEO Meta Tags
    document.title = 'Preguntas Frecuentes - VaciadoPro Barcelona | FAQ Vaciado Pisos';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Resuelve tus dudas sobre nuestros servicios de vaciado en Barcelona. Preguntas frecuentes sobre precios, tiempos y procedimientos de vaciado de pisos.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'FAQ vaciado Barcelona, preguntas vaciado pisos, dudas vaciado Barcelona, precios vaciado Barcelona');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/faq`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "Preguntas Frecuentes - VaciadoPro Barcelona",
      "description": "Preguntas frecuentes sobre servicios de vaciado de pisos en Barcelona",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/faq`,
      "mainEntity": [
        {
          "@type": "Question",
          "name": "¿Cuánto cuesta el servicio de vaciado?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El precio depende del volumen y tipo de enseres. Si los objetos tienen valor suficiente, el servicio puede ser gratuito. Ofrecemos presupuesto sin compromiso."
          }
        },
        {
          "@type": "Question",
          "name": "¿En cuánto tiempo realizan el vaciado?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Dependiendo del tamaño del inmueble, entre 2-8 horas. Para pisos estándar, normalmente completamos el trabajo en una jornada."
          }
        },
        {
          "@type": "Question",
          "name": "¿Qué incluye el servicio?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Incluye retirada de muebles, electrodomésticos, ropa y enseres, limpieza básica, transporte y gestión de residuos según normativa municipal."
          }
        },
        {
          "@type": "Question",
          "name": "¿Trabajan en toda Barcelona?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, ofrecemos servicios en toda Barcelona y área metropolitana. Consulta disponibilidad para tu zona específica."
          }
        },
        {
          "@type": "Question",
          "name": "¿Necesito estar presente durante el vaciado?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No es imprescindible, pero recomendamos supervisión inicial para indicar qué conservar. Trabajamos con total profesionalidad y confianza."
          }
        }
      ]
    });
    document.head.appendChild(schemaScript);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const faqItems = [
    {
      question: '¿Qué hacéis con los muebles y enseres?',
      answer: `<p class="mb-4">Tenemos un proceso responsable y ecológico para gestionar todos los enseres:</p>
               <ul class="list-disc list-inside space-y-2 mb-4">
                 <li><strong>Donación:</strong> Los muebles y objetos en buen estado los donamos a organizaciones benéficas y familias necesitadas</li>
                 <li><strong>Reutilización:</strong> Los enseres con valor comercial se venden en mercados de segunda mano</li>
                 <li><strong>Reciclaje:</strong> Los materiales reciclables se llevan al Punto Limpio correspondiente</li>
                 <li><strong>Gestión de residuos:</strong> Solo llevamos a vertedero autorizado aquello que no se puede reutilizar ni reciclar</li>
               </ul>
               <p>Nuestro objetivo es minimizar el impacto ambiental y dar una segunda vida a los objetos siempre que sea posible.</p>`
    },
    {
      question: '¿El servicio es realmente GRATIS?',
      answer: `<p class="mb-4">Sí, el servicio puede ser completamente gratuito cuando el valor de los muebles y enseres reutilizables cubre el coste del vaciado y nos da un beneficio.</p>
               <p class="mb-4"><strong>¿Cómo funciona?</strong></p>
               <ul class="list-disc list-inside space-y-2 mb-4">
                 <li>Evaluamos el valor de muebles, electrodomésticos y objetos</li>
                 <li>Si el valor estimado iguala o supera el coste del servicio, es GRATIS</li>
                 <li>Te damos una valoración honesta y transparente antes de empezar</li>
                 <li>No hay letra pequeña ni costes ocultos</li>
               </ul>
               <p>En muchos casos, especialmente con muebles de calidad o electrodomésticos funcionales, el servicio resulta gratuito.</p>`
    },
    {
      question: '¿Qué pasa si mis muebles no tienen valor?',
      answer: `<p class="mb-4">Si los enseres no tienen valor de reventa, te ofrecemos el presupuesto más ajustado del mercado.</p>
               <p class="mb-4"><strong>Nuestras ventajas:</strong></p>
               <ul class="list-disc list-inside space-y-2 mb-4">
                 <li>Precios hasta un 40% más económicos que la competencia</li>
                 <li>Sin sorpresas: el precio final es el acordado inicialmente</li>
                 <li>Incluye carga, transporte y gestión de residuos</li>
                 <li>Posibilidad de pago fraccionado en casos especiales</li>
               </ul>
               <p>Siempre buscamos la forma más económica para nuestros clientes, incluso cuando el servicio no puede ser gratuito.</p>`
    },
    {
      question: '¿Cuánto tiempo tardáis en vaciar un piso?',
      answer: `<p class="mb-4">La mayoría de vaciados se completan en <strong>un solo día</strong>, dependiendo del tamaño y cantidad de enseres.</p>
               <p class="mb-4"><strong>Tiempos estimados:</strong></p>
               <ul class="list-disc list-inside space-y-2 mb-4">
                 <li><strong>Piso pequeño (1-2 habitaciones):</strong> 2-4 horas</li>
                 <li><strong>Piso mediano (3 habitaciones):</strong> 4-6 horas</li>
                 <li><strong>Piso grande (4+ habitaciones):</strong> 6-8 horas</li>
                 <li><strong>Casos especiales (Síndrome de Diógenes):</strong> 1-2 días</li>
               </ul>
               <p>Trabajamos de forma eficiente pero cuidadosa, respetando el inmueble y las pertenencias que desees conservar.</p>`
    },
    {
      question: '¿Dais servicio en mi barrio/ciudad?',
      answer: `<p class="mb-4">¡Sí! Damos servicio en <strong>toda Barcelona ciudad y área metropolitana</strong>.</p>
               <p class="mb-4"><strong>Zonas de cobertura:</strong></p>
               <ul class="list-disc list-inside space-y-2 mb-4">
                 <li><strong>Barcelona ciudad:</strong> Todos los distritos (Eixample, Gràcia, Sants-Montjuïc, Sant Martí, Ciutat Vella, Les Corts, Nou Barris, Sant Andreu, Sarrià-Sant Gervasi, Horta-Guinardó)</li>
                 <li><strong>Área metropolitana:</strong> L'Hospitalet, Badalona, Santa Coloma, Cornellà, Esplugues, Sant Boi, Viladecans, El Prat, y más</li>
                 <li><strong>Otras localidades:</strong> Consulta disponibilidad para tu zona</li>
               </ul>
               <p>Servicio disponible 7 días a la semana, incluidos festivos con suplemento.</p>`
    },
    {
      question: '¿Tengo que pedir algún permiso?',
      answer: `<p class="mb-4"><strong>No te preocupes por los permisos, nosotros nos encargamos de todo.</strong></p>
               <p class="mb-4"><strong>Gestiones que realizamos:</strong></p>
               <ul class="list-disc list-inside space-y-2 mb-4">
                 <li><strong>Permisos de carga y descarga:</strong> Si es necesario reservar espacio en la calle</li>
                 <li><strong>Autorizaciones del ayuntamiento:</strong> Para contenedores o vehículos grandes</li>
                 <li><strong>Coordinación con portería:</strong> Horarios y accesos al edificio</li>
                 <li><strong>Seguros y responsabilidad civil:</strong> Cobertura completa durante el servicio</li>
               </ul>
               <p>Solo necesitas estar presente el día del vaciado o designar a una persona de confianza con las llaves.</p>`
    },
    {
      question: "¿Qué hacen con los residuos y muebles que recogen?",
      answer: "Seguimos un protocolo de gestión responsable: 1) Los enseres en buen estado los donamos a organizaciones benéficas, 2) Los materiales reciclables los llevamos a puntos limpios autorizados, 3) Los residuos especiales se gestionan según normativa. También coordinamos con el servicio municipal de recogida de muebles del Ayuntamiento de Barcelona cuando es necesario. Proporcionamos certificados de gestión ambiental."
    },
    {
      question: "¿Ayudan con pisos que llevan mucho tiempo vacíos?",
      answer: "Sí, somos especialistas en vaciado de pisos que han estado desocupados durante largos períodos. El problema de la vivienda vacía es común en Barcelona, y nosotros facilitamos que los propietarios puedan poner sus inmuebles en condiciones para alquiler o venta. Incluimos limpieza profunda, desinfección y pequeñas reparaciones si es necesario."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Preguntas frecuentes
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Resuelve todas tus dudas sobre nuestro servicio de vaciado de pisos en Barcelona. 
              Si no encuentras la respuesta que buscas, ¡contáctanos! También puedes conocer más sobre 
              <a href="/empresa" className="text-green-600 hover:text-green-700 font-medium ml-1 mr-1">
                nuestra empresa
              </a>
              y consultar todos 
              <a href="/servicios" className="text-green-600 hover:text-green-700 font-medium ml-1">
                nuestros servicios
              </a>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                href="https://wa.me/34690220944?text=Hola, tengo algunas preguntas sobre el vaciado de pisos en Barcelona" 
                variant="primary" 
                size="lg"
                className="flex items-center gap-3"
                target="_blank"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Preguntar por WhatsApp
              </Button>
              <Button 
                href="tel:+34690220944" 
                variant="secondary" 
                size="lg"
                className="flex items-center gap-3 !bg-white !text-green-600 hover:!bg-gray-100 border-2 border-green-600"
              >
                <i className="ri-phone-line text-xl"></i>
                Llamar Ahora
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Accordion items={faqItems} />
          </div>
        </div>
      </section>

      {/* Additional Help Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-green-50 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-question-line text-white text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                ¿No encuentras la respuesta que buscas?
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Nuestro equipo está disponible para resolver cualquier duda específica sobre tu caso. 
                Cada situación es única y nos adaptamos a tus necesidades. Conoce más sobre 
                <a href="/empresa" className="text-green-600 hover:text-green-700 font-medium ml-1 mr-1">
                  nuestro equipo profesional
                </a>
                y descubre todos 
                <a href="/servicios" className="text-green-600 hover:text-green-700 font-medium ml-1">
                  nuestros servicios especializados
                </a>.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  href="https://wa.me/34690220944?text=Hola, quiero mi presupuesto personalizado para vaciado de piso en Barcelona" 
                  variant="primary" 
                  size="lg"
                  className="flex items-center gap-3"
                  target="_blank"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  Presupuesto Personalizado
                </Button>
                <Button 
                  href="/contacto" 
                  variant="secondary" 
                  size="lg"
                  className="flex items-center gap-3 !bg-white !text-green-600 hover:!bg-gray-100 border-2 border-green-600"
                >
                  <i className="ri-mail-line text-xl"></i>
                  Contactar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Tips Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Consejos útiles antes del vaciado
              </h2>
              <p className="text-xl text-gray-600">
                Pequeños preparativos que pueden ayudarte a optimizar el servicio
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-camera-line text-blue-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Envía Fotos</h3>
                <p className="text-gray-600 leading-relaxed">
                  Comparte fotos o vídeo por WhatsApp para una valoración más precisa y rápida. 
                  También puedes usar nuestro 
                  <a href="/contacto" className="text-green-600 hover:text-green-700 font-medium ml-1">
                    formulario de contacto
                  </a>.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-heart-line text-green-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Separa lo Importante</h3>
                <p className="text-gray-600 leading-relaxed">
                  Identifica objetos de valor sentimental o documentos importantes antes de nuestra llegada.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-time-line text-purple-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Planifica el Horario</h3>
                <p className="text-gray-600 leading-relaxed">
                  Coordina con portería y vecinos para evitar molestias durante el vaciado.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            ¿Listo para empezar? pide tu valoración gratuita
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Resolvemos tu problema de vaciado de forma rápida, profesional y al mejor precio. 
            ¡Contacta ahora y recibe tu presupuesto en menos de 2 horas! Conoce más sobre 
            <a href="/empresa" className="text-green-200 hover:text-white font-medium ml-1 mr-1">
              nuestro equipo
            </a>
            y consulta todos 
            <a href="/servicios" className="text-green-200 hover:text-white font-medium ml-1">
              nuestros servicios
            </a>.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              href="https://wa.me/34690220944" 
              variant="secondary" 
              size="lg"
              className="flex items-center gap-3"
              target="_blank"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Valoración por WhatsApp
            </Button>
            <Button 
              href="tel:+34690220944" 
              variant="primary" 
              size="lg"
              className="flex items-center gap-3"
            >
              <i className="ri-phone-line text-xl"></i>
              Consulta Telefónica
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
